/**
 * Syntax highlighting for the markdown source shown in the editor.
 *
 * The editor is a plain `<textarea>` with transparent text sitting on top of a
 * div that renders the same characters with `<span>`s around them. That only
 * works if the two agree on every character — so this never adds, removes or
 * reorders anything. It returns ranges over the original string and nothing
 * else.
 *
 * ## Why a character map rather than nested tokens
 *
 * Markdown constructs overlap in ways a tree cannot express: emphasis inside a
 * heading inside a blockquote, a link whose label is bold, a fence that
 * swallows everything. Rather than resolve that, every rule paints its range
 * onto a per-character array with a priority, highest wins, and adjacent
 * characters of the same type are coalesced into runs at the end. Conflicts
 * resolve by construction, and a rule that gets something wrong can only ever
 * mis-colour — it can never lose text.
 *
 * Priorities run roughly: syntax markers (80–95) beat structural spans
 * (52–75), which beat whole-block tints (20–30). Footnote bodies sit at the
 * bottom on purpose so emphasis inside a citation still shows.
 */

import { findFootnoteEnd } from "@/components/editor/extensions/footnote-markdown";
import {
  fencedBlockExpression,
  headingSlideExpression,
  isSlideInfo,
  slideContainerExpression,
} from "@/lib/slides";

export type TokenType =
  | "marker"
  /**
   * The lines and markers that make something a slide — ```` ```slide ````,
   * `:::slide`, `{slide}`. Their own type rather than `marker` so they read as
   * a label on the manuscript rather than as more syntax.
   */
  | "slide-marker"
  /**
   * Filler for characters inside a slide block that no other rule claimed.
   * Exists so the block has no bare text nodes — see `inSlide` below.
   */
  | "slide"
  /**
   * The `#`s of an ATX heading, split out from `marker` so the stylesheet can
   * treat them differently from every other piece of syntax: they carry the
   * heading's level, and they are the one marker that hangs in the margin.
   */
  | "heading-marker"
  | "heading"
  /** Body of a `>` blockquote line — content tint, markers stay separate. */
  | "quote"
  | "code"
  | "emphasis"
  | "strong"
  | "strike"
  | "link"
  | "url"
  | "footnote"
  | "table";

export type HighlightRange = {
  start: number;
  end: number;
  type: TokenType;
  /** Heading depth, 1–6, on `heading-marker` runs only. */
  level?: number;
  /**
   * This run sits inside a slide block.
   *
   * Carried alongside the type rather than replacing it because a slide's
   * contents keep their ordinary colours — a heading in a slide is still a
   * heading. The backdrop renders ranges as flat sibling spans, so the only way
   * to give the block a continuous background is for every character in it to
   * be inside *some* span carrying this flag. That is what the `slide` filler
   * type is for: without it the unclaimed gaps would be bare text nodes and the
   * card would have holes punched through it.
   */
  inSlide?: boolean;
};

type Mark = (
  start: number,
  end: number,
  type: TokenType,
  priority: number,
  level?: number,
) => void;

function visitMatches(source: string, expression: RegExp, visit: (match: RegExpExecArray) => void) {
  expression.lastIndex = 0;
  let match = expression.exec(source);
  while (match) {
    visit(match);
    // A zero-length match would spin here forever.
    if (match[0].length === 0) expression.lastIndex += 1;
    match = expression.exec(source);
  }
}

/**
 * Footnotes, `^[note text]`.
 *
 * Scanned by hand rather than with a regex because the body may contain
 * balanced brackets and backslash escapes — the same rules the serializer
 * applies, so `findFootnoteEnd` is shared with it rather than reimplemented.
 */
function markFootnotes(source: string, mark: Mark) {
  let index = source.indexOf("^[");
  while (index !== -1) {
    const escaped = index > 0 && source[index - 1] === "\\";
    const end = escaped ? -1 : findFootnoteEnd(source, index + 2);
    if (end === -1) {
      index = source.indexOf("^[", index + 2);
      continue;
    }
    mark(index, index + 2, "marker", 94);
    mark(index + 2, end, "footnote", 20);
    mark(end, end + 1, "marker", 94);
    index = source.indexOf("^[", end + 1);
  }
}

function markInline(source: string, mark: Mark) {
  visitMatches(source, /(`+)([^\n]*?[^`])\1(?!`)/g, (match) => {
    const start = match.index;
    mark(start, start + match[1].length, "marker", 90);
    mark(start + match[1].length, start + match[0].length - match[1].length, "code", 75);
    mark(start + match[0].length - match[1].length, start + match[0].length, "marker", 90);
  });

  visitMatches(source, /(!?)\[([^\]\n]+)\]\(([^)\n]+)\)/g, (match) => {
    const start = match.index;
    const bang = match[1].length;
    const labelStart = start + bang + 1;
    const urlStart = labelStart + match[2].length + 2;
    mark(start, labelStart, "marker", 88);
    mark(labelStart, labelStart + match[2].length, "link", 62);
    mark(labelStart + match[2].length, urlStart, "marker", 88);
    mark(urlStart, urlStart + match[3].length, "url", 65);
    mark(urlStart + match[3].length, start + match[0].length, "marker", 88);
  });

  visitMatches(source, /<(https?:\/\/[^>\s]+|mailto:[^>\s]+|[^<>\s]+@[^<>\s]+)>/g, (match) => {
    mark(match.index, match.index + 1, "marker", 88);
    mark(match.index + 1, match.index + match[0].length - 1, "url", 65);
    mark(match.index + match[0].length - 1, match.index + match[0].length, "marker", 88);
  });

  const paired: Array<[RegExp, TokenType, number]> = [
    [/(\*\*|__)(?=\S)(.+?\S)\1/g, "strong", 56],
    [/(~~)(?=\S)(.+?\S)\1/g, "strike", 54],
    [/(\*|_)(?=\S)([^\n]*?\S)\1/g, "emphasis", 52],
  ];

  for (const [expression, type, priority] of paired) {
    visitMatches(source, expression, (match) => {
      const start = match.index;
      const markerLength = match[1].length;
      mark(start, start + markerLength, "marker", 86);
      mark(start + markerLength, start + match[0].length - markerLength, type, priority);
      mark(start + match[0].length - markerLength, start + match[0].length, "marker", 86);
    });
  }

  visitMatches(source, /(^|[^\\])\\([!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~])/gm, (match) => {
    const slash = match.index + match[1].length;
    mark(slash, slash + 1, "marker", 92);
  });
}

export function analyzeMarkdown(source: string): HighlightRange[] {
  if (!source) return [];

  const tokens: Array<TokenType | undefined> = new Array(source.length);
  const priorities = new Uint8Array(source.length);
  /** Parallel to `tokens`; 0 where the type carries no level. */
  const levels = new Uint8Array(source.length);
  /** Parallel to `tokens`; set once by the slide rule and never cleared. */
  const slideChars = new Uint8Array(source.length);
  const mark: Mark = (start, end, type, priority, level = 0) => {
    const safeStart = Math.max(0, start);
    const safeEnd = Math.min(source.length, end);
    for (let index = safeStart; index < safeEnd; index += 1) {
      if (priority >= priorities[index]) {
        priorities[index] = priority;
        tokens[index] = type;
        levels[index] = level;
      }
    }
  };

  // Fences first, and every later block rule checks this map. Inside a code
  // block a `#` is a comment, not a heading.
  const fencedLines = new Uint8Array(source.length);
  visitMatches(source, fencedBlockExpression(), (match) => {
    const start = match.index;
    const end = start + match[0].length;
    const opening = start + match[1].length;
    const closingStart = source.lastIndexOf(match[2], end);

    // A slide is a fence that is deliberately *not* opaque. Marking it as code
    // would freeze its contents into monospace, and a slide is prose — its
    // bullets and emphasis have to keep colouring like the rest of the
    // document. So it skips `fencedLines`, and every later block rule runs
    // straight through it as though the fence were not there.
    if (isSlideInfo(match[3] ?? "")) {
      for (let index = start; index < end; index += 1) slideChars[index] = 1;
      // Filler, at a priority below every content rule, so anything real that
      // paints later wins and only the gaps keep this type.
      mark(start, end, "slide", 12);
      // The whole info line, not just the backticks: `\`\`\`slide` reads as one
      // label rather than as syntax plus a word.
      mark(opening, opening + match[2].length + (match[3] ?? "").length, "slide-marker", 90);
      if (closingStart > opening) {
        mark(closingStart, closingStart + match[2].length, "slide-marker", 90);
      }
      return;
    }

    for (let index = start; index < end; index += 1) fencedLines[index] = 1;
    mark(start, end, "code", 70);
    mark(opening, opening + match[2].length, "marker", 95);
    if (closingStart > opening) mark(closingStart, closingStart + match[2].length, "marker", 95);
  });

  // `:::slide`, the wrapper that keeps its contents in the manuscript. Painted
  // exactly like the slide fence — markers as a label, contents left to colour
  // as ordinary prose — because that is what it is: a slide whose words happen
  // to still be part of the sermon. It runs after the fences so a container
  // quoted inside a code block stays code.
  visitMatches(source, slideContainerExpression(), (match) => {
    if (fencedLines[match.index]) return;
    const start = match.index;
    const end = start + match[0].length;
    for (let index = start; index < end; index += 1) slideChars[index] = 1;
    mark(start, end, "slide", 12);

    // The whole opening line, so `:::slide` reads as one label.
    const openerEnd = source.indexOf("\n", start);
    mark(start + match[1].length, openerEnd === -1 ? end : openerEnd, "slide-marker", 90);
    const closerStart = source.lastIndexOf("\n", end - 1) + 1;
    if (closerStart > start) mark(closerStart + match[4].length, end, "slide-marker", 90);
  });

  visitMatches(source, /^( {0,3})(#{1,6})(?:[ \t]+)(.*)$/gm, (match) => {
    if (fencedLines[match.index]) return;
    const markerStart = match.index + match[1].length;
    const contentStart = markerStart + match[2].length;
    mark(markerStart, contentStart, "heading-marker", 80, match[2].length);
    mark(contentStart, match.index + match[0].length, "heading", 30);
  });

  // `{slide}` on a heading — the annotate-don't-replace way of making a slide.
  // Painted over the heading text it sits in, so it reads as a chip on the line
  // rather than as part of the title.
  visitMatches(source, headingSlideExpression(), (match) => {
    if (fencedLines[match.index]) return;
    const markerStart = match.index + match[1].length + match[2].length;
    mark(markerStart, markerStart + match[3].length, "slide-marker", 86);
  });

  visitMatches(source, /^( {0,3})(>+)([ \t]?)(.*)$/gm, (match) => {
    if (fencedLines[match.index]) return;
    const markerStart = match.index + match[1].length;
    mark(markerStart, markerStart + match[2].length, "marker", 80);
    // Soft content tint for the quoted words — same Ulysses idea as heading
    // text. Priority sits under emphasis/strong/links so nested markup still
    // paints on top.
    const contentStart = markerStart + match[2].length + match[3].length;
    const lineEnd = match.index + match[0].length;
    if (contentStart < lineEnd) mark(contentStart, lineEnd, "quote", 22);
  });

  visitMatches(source, /^(\s*)([-+*]|\d{1,9}[.)])([ \t]+)/gm, (match) => {
    if (!fencedLines[match.index]) {
      const start = match.index + match[1].length;
      mark(start, start + match[2].length, "marker", 80);
    }
  });

  visitMatches(source, /^(\s*[-+*][ \t]+)(\[[ xX]\])/gm, (match) => {
    if (!fencedLines[match.index]) {
      const start = match.index + match[1].length;
      mark(start, start + match[2].length, "marker", 82);
    }
  });

  visitMatches(source, /^( {0,3})(-{3,}|\*{3,}|_{3,})[ \t]*$/gm, (match) => {
    if (!fencedLines[match.index]) mark(match.index, match.index + match[0].length, "marker", 72);
  });

  visitMatches(source, /^( {4}|\t)(.+)$/gm, (match) => {
    if (!fencedLines[match.index]) mark(match.index, match.index + match[0].length, "code", 68);
  });

  visitMatches(source, /^([^\n|]*\|[^\n]*)\n( *\|? *:?-{3,}:? *(?:\| *:?-{3,}:? *)+\|? *)$/gm, (match) => {
    if (fencedLines[match.index]) return;
    mark(match.index, match.index + match[0].length, "table", 24);
    const separatorStart = match.index + match[1].length + 1;
    mark(separatorStart, separatorStart + match[2].length, "marker", 58);
  });

  visitMatches(source, /^ {0,3}\[([^\]]+)\]:[ \t]*(\S+)(?:[ \t]+(?:"[^"]*"|'[^']*'|\([^)]*\)))?[ \t]*$/gm, (match) => {
    if (fencedLines[match.index]) return;
    const labelStart = match.index + match[0].indexOf("[");
    const urlStart = match.index + match[0].indexOf(match[2]);
    mark(labelStart, labelStart + match[1].length + 2, "marker", 82);
    mark(urlStart, urlStart + match[2].length, "url", 64);
  });

  markInline(source, mark);
  markFootnotes(source, mark);

  const ranges: HighlightRange[] = [];
  let index = 0;
  while (index < tokens.length) {
    const type = tokens[index];
    if (!type) {
      index += 1;
      continue;
    }
    // Two adjacent headings of different depth must not coalesce into one run,
    // or `#` and `###` would share a hanging offset. Nor may a run straddle a
    // slide boundary, or the block's background would bleed into the prose.
    const level = levels[index];
    const inSlide = slideChars[index];
    let end = index + 1;
    while (
      end < tokens.length &&
      tokens[end] === type &&
      levels[end] === level &&
      slideChars[end] === inSlide
    ) {
      end += 1;
    }

    const range: HighlightRange = { start: index, end, type };
    if (level) range.level = level;
    if (inSlide) range.inSlide = true;
    ranges.push(range);
    index = end;
  }

  return ranges;
}

export type HighlightChunk = {
  text: string;
  /** Offsets are relative to this chunk, not the document. */
  ranges: HighlightRange[];
};

/** Smallest chunk worth isolating. Below this the per-chunk React overhead
 * costs more than the reconciliation it saves. */
const MIN_CHUNK_CHARS = 400;

/**
 * Break the document into independently renderable pieces at blank lines.
 *
 * The backdrop is the one thing in this editor that touches the whole document
 * on every keystroke, and a long sermon is tens of thousands of characters —
 * re-rendering every span of it per character is the cost that would make this
 * feel worse than what it replaces. Chunks let React skip the untouched
 * majority: each one is memoised on its text and its ranges, and typing inside
 * a paragraph changes only the chunk containing it.
 *
 * Ranges come back relative to the chunk for the same reason. Absolute offsets
 * would change in every chunk after an edit, so nothing downstream of the
 * cursor could ever bail out of re-rendering.
 */
export function chunkHighlights(source: string, ranges: HighlightRange[]): HighlightChunk[] {
  if (!source) return [];

  const boundaries: number[] = [0];
  let rangeCursor = 0;

  visitMatches(source, /\n[ \t]*\n/g, (match) => {
    const offset = match.index + match[0].length;
    if (offset - boundaries[boundaries.length - 1] < MIN_CHUNK_CHARS) return;

    // Never cut through a highlighted run — a fenced block or a table spans
    // blank lines, and splitting one would hand half a range to each side.
    while (rangeCursor < ranges.length && ranges[rangeCursor].end <= offset) rangeCursor += 1;
    const straddling = ranges[rangeCursor];
    if (straddling && straddling.start < offset && straddling.end > offset) return;

    boundaries.push(offset);
  });

  boundaries.push(source.length);

  const chunks: HighlightChunk[] = [];
  let cursor = 0;

  for (let index = 1; index < boundaries.length; index += 1) {
    const start = boundaries[index - 1];
    const end = boundaries[index];
    if (end <= start) continue;

    while (cursor < ranges.length && ranges[cursor].end <= start) cursor += 1;

    const local: HighlightRange[] = [];
    for (let scan = cursor; scan < ranges.length && ranges[scan].start < end; scan += 1) {
      const range = ranges[scan];
      local.push({
        start: Math.max(range.start, start) - start,
        end: Math.min(range.end, end) - start,
        type: range.type,
        level: range.level,
        inSlide: range.inSlide,
      });
    }

    chunks.push({ text: source.slice(start, end), ranges: local });
  }

  return chunks;
}
